import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gem, TrendingUp, TrendingDown, RefreshCw, Filter, Award, AlertTriangle, Package, Eye } from 'lucide-react';
import api, { API_URL } from '../../services/api';

const RapportBijoux = () => {
  const navigate = useNavigate();
  const [vue, setVue] = useState('modeles'); // 'modeles' | 'unitaires'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState(today);
  const [filtreMarge, setFiltreMarge] = useState('');
  const [refFilter, setRefFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fmt = (m) => parseFloat(m||0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' DH';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      if (vue === 'unitaires') {
        if (filtreMarge) params.append('filtre', filtreMarge);
        if (refFilter) params.append('reference_article', refFilter);
        params.append('limit', '200');
      } else {
        params.append('limit', '100');
      }
      const url = vue === 'modeles' ? `${API_URL}/rapports/bijoux?${params.toString()}` : `${API_URL}/rapports/bijoux/unitaires?${params.toString()}`;
      const r = await api.get(url);
      setData(r.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [vue, filtreMarge, refFilter]);

  const statutColor = (s) => {
    if (s === 'excellent') return 'bg-green-100 text-green-700';
    if (s === 'normal') return 'bg-blue-100 text-blue-700';
    if (s === 'faible') return 'bg-orange-100 text-orange-700';
    if (s === 'perte') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-500';
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div></div>;

  // VUE MODELES
  if (vue === 'modeles') {
    const refs = data?.references || [];
    const totalGlobal = data?.total_global || 0;
    const nbVentesTotal = data?.nb_ventes_total || 0;

    return (
      <div className="min-h-screen bg-gray-100 pb-8">
        <div className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold">Rapport Bijoux</h1>
                <p className="text-amber-200 text-sm">Performance par modele (reference article) + bijou par bijou</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowFilters(!showFilters)} className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg">
                  <Filter className="h-4 w-4" /><span>Periode</span>
                </button>
                <button onClick={loadData} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg"><RefreshCw className="h-4 w-4" /></button>
              </div>
            </div>

            {/* Toggle vue */}
            <div className="mt-4 flex gap-2">
              <button onClick={() => setVue('modeles')}
                className={`px-4 py-2 rounded-lg font-medium ${vue === 'modeles' ? 'bg-white text-amber-700' : 'bg-white/20 text-white'}`}>
                Par modele
              </button>
              <button onClick={() => setVue('unitaires')}
                className={`px-4 py-2 rounded-lg font-medium ${vue === 'unitaires' ? 'bg-white text-amber-700' : 'bg-white/20 text-white'}`}>
                Bijou par bijou
              </button>
            </div>

            {showFilters && (
              <div className="mt-4 bg-white/10 rounded-xl p-4 flex flex-col sm:flex-row items-end gap-3 flex-wrap">
                <div><label className="block text-xs text-amber-200 mb-1">Du</label><input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" /></div>
                <div><label className="block text-xs text-amber-200 mb-1">Au</label><input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" /></div>
                <button onClick={loadData} className="px-6 py-2 bg-white text-amber-700 font-medium rounded-lg">Appliquer</button>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
          <div className="bg-gradient-to-r from-amber-600 to-yellow-600 rounded-xl p-5 flex items-center justify-between shadow-lg">
            <div className="text-white">
              <p className="font-medium">TOTAL CA</p>
              <p className="text-xs opacity-70">{refs.length} references - {nbVentesTotal} ventes</p>
            </div>
            <p className="text-3xl font-black text-white">{fmt(totalGlobal)}</p>
          </div>

          {refs.length >= 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {refs.slice(0, 3).filter(r => r.marge_brute > 0).slice(0, 3).map((r, i) => (
                <div key={r.reference_article} className={`rounded-xl shadow-sm border p-5 ${i === 0 ? 'bg-yellow-50 border-yellow-300' : i === 1 ? 'bg-gray-50 border-gray-300' : 'bg-orange-50 border-orange-300'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Award className={`h-6 w-6 ${i === 0 ? 'text-yellow-500' : i === 1 ? 'text-gray-400' : 'text-orange-500'}`} />
                    <span className="text-xs font-bold text-gray-500">#{i+1} Top marge</span>
                  </div>
                  <p className="font-black text-lg text-gray-900">{r.reference_article}</p>
                  <p className="text-xs text-gray-500">{r.nb_ventes} ventes - {fmt(r.total_ca)}</p>
                  <p className="text-2xl font-black text-green-700 mt-2">{fmt(r.marge_brute)}</p>
                  <p className="text-xs text-green-600">Marge {r.marge_pct}%</p>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border">
            <div className="px-5 py-4 border-b">
              <h2 className="font-bold text-gray-800 flex items-center gap-2"><Gem className="h-5 w-5 text-amber-600" />REFERENCES DETAILLEES ({refs.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">#</th>
                    <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Reference</th>
                    <th className="px-3 py-3 text-center text-xs text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-3 text-center text-xs text-gray-500 uppercase">Ventes</th>
                    <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">CA</th>
                    <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Cout</th>
                    <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Marge</th>
                    <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">%</th>
                    <th className="px-3 py-3 text-center text-xs text-gray-500 uppercase">Alertes</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {refs.map((r, i) => (
                    <tr key={r.reference_article} className={`hover:bg-gray-50 ${r.reference_article === '(sans reference)' ? 'bg-yellow-50' : ''}`}>
                      <td className="px-3 py-2 text-gray-400 font-bold">{i+1}</td>
                      <td className="px-3 py-2">
                        <p className="font-bold text-gray-900">{r.reference_article}</p>
                        <p className="text-xs text-gray-400">{r.categorie || '-'} - {r.couleur_or || '-'}</p>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{r.matiere || '-'}</span>
                      </td>
                      <td className="px-3 py-2 text-center font-bold">{r.nb_ventes}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmt(r.total_ca)}</td>
                      <td className="px-3 py-2 text-right text-red-600">{r.total_cout > 0 ? fmt(r.total_cout) : <span className="text-gray-300">-</span>}</td>
                      <td className={`px-3 py-2 text-right font-black ${r.marge_brute > 0 ? 'text-green-700' : r.marge_brute < 0 ? 'text-red-700' : 'text-gray-400'}`}>
                        {r.nb_avec_cout > 0 ? fmt(r.marge_brute) : <span className="text-gray-300">N/A</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500">
                        {r.nb_avec_cout > 0 ? `${r.marge_pct}%` : '-'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex justify-center gap-1">
                          {r.nb_sans_cout > 0 && (
                            <button
                              onClick={() => { setRefFilter(r.reference_article === '(sans reference)' ? '' : r.reference_article); setFiltreMarge('sans_cout'); setVue('unitaires'); }}
                              title={`${r.nb_sans_cout} pièce(s) sans coût connu : impossible de calculer la marge (vente orpheline ou coût de revient jamais renseigné). La marge affichée pour cette référence n'inclut pas ces pièces. Cliquer pour compléter le coût.`}
                              className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 cursor-pointer hover:bg-yellow-200 hover:ring-1 hover:ring-yellow-400 underline decoration-dotted underline-offset-2">
                              {r.nb_sans_cout}
                            </button>
                          )}
                          {r.bijoux_perte > 0 && (
                            <button
                              onClick={() => { setRefFilter(r.reference_article === '(sans reference)' ? '' : r.reference_article); setFiltreMarge('perte'); setVue('unitaires'); }}
                              title={`${r.bijoux_perte} pièce(s) vendue(s) à perte : le prix encaissé est inférieur au coût de revient. Cliquer pour voir la/les pièce(s) et corriger (prix, coût ou réétiquetage).`}
                              className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 cursor-pointer hover:bg-red-200 hover:ring-1 hover:ring-red-400 underline decoration-dotted underline-offset-2">
                              ⚠{r.bijoux_perte}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // VUE BIJOUX UNITAIRES
  const ventes = data?.ventes || [];
  const t = data?.totaux || {};

  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      <div className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Rapport Bijoux</h1>
              <p className="text-amber-200 text-sm">Marge par bijou individuel (id_produit)</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowFilters(!showFilters)} className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg">
                <Filter className="h-4 w-4" /><span>Filtres</span>
              </button>
              <button onClick={loadData} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg"><RefreshCw className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button onClick={() => setVue('modeles')} className={`px-4 py-2 rounded-lg font-medium ${vue === 'modeles' ? 'bg-white text-amber-700' : 'bg-white/20 text-white'}`}>Par modele</button>
            <button onClick={() => setVue('unitaires')} className={`px-4 py-2 rounded-lg font-medium ${vue === 'unitaires' ? 'bg-white text-amber-700' : 'bg-white/20 text-white'}`}>Bijou par bijou</button>
          </div>

          {showFilters && (
            <div className="mt-4 bg-white/10 rounded-xl p-4 flex flex-col sm:flex-row items-end gap-3 flex-wrap">
              <div><label className="block text-xs text-amber-200 mb-1">Du</label><input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" /></div>
              <div><label className="block text-xs text-amber-200 mb-1">Au</label><input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" /></div>
              <div><label className="block text-xs text-amber-200 mb-1">Reference modele</label><input type="text" placeholder="Ex: BAGB21" value={refFilter} onChange={e => setRefFilter(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30 placeholder-amber-200" /></div>
              <button onClick={loadData} className="px-6 py-2 bg-white text-amber-700 font-medium rounded-lg">Appliquer</button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Ventes</p>
            <p className="text-2xl font-black text-gray-900">{t.nb_ventes || 0}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">CA total</p>
            <p className="text-xl font-black text-amber-700">{fmt(t.ca_total)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Cout total</p>
            <p className="text-xl font-black text-red-600">{fmt(t.cout_total)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Marge</p>
            <p className={`text-xl font-black ${t.marge_totale >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmt(t.marge_totale)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Alertes</p>
            <p className="text-sm font-bold">
              {t.nb_perte > 0 && <span className="text-red-600">⚠ {t.nb_perte} perte</span>}
              {t.nb_sans_cout > 0 && <span className="text-yellow-600 ml-1">{t.nb_sans_cout} sans cout</span>}
              {(t.nb_perte === 0 && t.nb_sans_cout === 0) && <span className="text-green-600">✓ OK</span>}
            </p>
          </div>
        </div>

        {/* Filtres status */}
        <div className="flex flex-wrap gap-2">
          {[
            { key: '', label: 'Tous' },
            { key: 'top_marge', label: '✨ Excellente marge (≥60%)', color: 'green' },
            { key: 'marge_faible', label: '⚠ Marge faible (<30%)', color: 'orange' },
            { key: 'perte', label: '❌ Pertes', color: 'red' },
            { key: 'sans_cout', label: '❓ Sans cout', color: 'yellow' },
          ].map(f => (
            <button key={f.key} onClick={() => setFiltreMarge(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filtreMarge === f.key ? 'bg-amber-600 text-white' : 'bg-white text-gray-700 border'}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Liste bijoux */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b">
            <h2 className="font-bold text-gray-800 flex items-center gap-2"><Package className="h-5 w-5 text-amber-600" />VENTES ({ventes.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Date</th>
                  <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">ID produit</th>
                  <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Bijou</th>
                  <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Boutique</th>
                  <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Prix vente</th>
                  <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Cout reel</th>
                  <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Marge</th>
                  <th className="px-3 py-3 text-center text-xs text-gray-500 uppercase">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {ventes.length === 0 ? (
                  <tr><td colSpan="8" className="px-3 py-8 text-center text-gray-500">Aucune vente</td></tr>
                ) : ventes.map(v => (
                  <tr key={v.vente_id}
                      onClick={() => v.id_produit && navigate(`/bijoux?search=${encodeURIComponent(v.id_produit)}&vendus=1`)}
                      title="Ouvrir cette pièce dans Pièces & Production (corriger prix/coût)"
                      className="hover:bg-amber-50 cursor-pointer">
                    <td className="px-3 py-2 text-gray-600 text-xs">{fmtDate(v.date_vente)}</td>
                    <td className="px-3 py-2">
                      <p className="font-mono font-bold text-amber-700">{v.id_produit}</p>
                      {v.reference_article && <p className="text-xs text-gray-400">{v.reference_article}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-900 text-xs">{v.nom}</p>
                      <div className="flex gap-1 mt-0.5">
                        {v.categorie && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{v.categorie}</span>}
                        {v.couleur_or && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700">{v.couleur_or}</span>}
                        {v.poids && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-700">{v.poids}g</span>}
                        {v.is_depot && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">Depot</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{v.boutique_nom || '-'}</td>
                    <td className="px-3 py-2 text-right font-bold">{fmt(v.prix_vente)}</td>
                    <td className="px-3 py-2 text-right text-red-600">
                      {v.cout !== null ? fmt(v.cout) : <span className="text-gray-300">?</span>}
                    </td>
                    <td className={`px-3 py-2 text-right font-bold ${v.marge > 0 ? 'text-green-700' : v.marge < 0 ? 'text-red-700' : 'text-gray-400'}`}>
                      {v.marge !== null ? (
                        <div>
                          <p>{fmt(v.marge)}</p>
                          <p className="text-xs font-normal">{v.marge_pct}%</p>
                        </div>
                      ) : <span className="text-gray-300">N/A</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span
                        title={v.statut_marge === 'perte' ? `Vendue à perte : coût ${fmt(v.cout)} > prix ${fmt(v.prix_vente)}.` :
                               v.statut_marge === 'faible' ? `Marge faible : ${v.marge_pct}% (seuil d'alerte : 30 %).` :
                               v.statut_marge === 'normal' ? 'Marge normale.' :
                               v.statut_marge === 'excellent' ? 'Excellente marge.' :
                               'Coût inconnu — marge non calculable.'}
                        className={`text-xs px-2 py-1 rounded-full font-medium ${statutColor(v.statut_marge)}`}>
                        {v.statut_marge === 'excellent' ? '✨ Excellent' :
                         v.statut_marge === 'normal' ? '✓ OK' :
                         v.statut_marge === 'faible' ? '⚠ Faible' :
                         v.statut_marge === 'perte' ? '❌ Perte' : '?'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RapportBijoux;
